import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import TextField from "@mui/material/TextField";
import TableSortLabel from "@mui/material/TableSortLabel";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import PrintIcon from "@mui/icons-material/Print";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import HandymanIcon from "@mui/icons-material/Handyman";
import { getRightworkAPI } from "../../requests/utilities/requests";
import { API_get_maintenance_assets } from "../../../../constants";
import LoadSpinner from "../../../../utilities/LoadSpinner";
import RightworkSelect from "../../../../components/shared/RightworkSelect";
import AssetDetailView from "./AssetDetailView";
import AssetDrawer from "./AssetDrawer";
import AssetImportModal from "./AssetImportModal";
import AssetLabelSheet from "./AssetLabelSheet";
import { assetMatches } from "./AssetPicker";
import { formatMoney, formatShortDate, isWarrantyExpired, formatMonthYear } from "./assetFormat";

function LabeledSelect({ id, label, className, ...selectProps }) {
  return (
    <div className={className}>
      <label id={`${id}-label`} htmlFor={id} className="mb-1 block text-xs font-medium text-slate-500">
        {label}
      </label>
      <RightworkSelect id={id} {...selectProps} />
    </div>
  );
}

// Table columns and how each sorts. Text sorts naturally ("A-2" before
// "A-10"); empty values always sort last.
const COLUMNS = [
  { id: "code", label: "Code", value: (a) => a.code, text: true },
  { id: "name", label: "Asset", value: (a) => a.name, text: true },
  { id: "location", label: "Area", value: (a) => a.location, text: true },
  { id: "make_model", label: "Make / Model", value: (a) => [a.manufacturer, a.model_number].filter(Boolean).join(" "), text: true },
  { id: "open", label: "Open", value: (a) => a.stats?.open_count ?? 0 },
  { id: "tickets", label: "Tickets (12 mo)", value: (a) => a.stats?.tickets_12mo ?? 0 },
  { id: "spend", label: "Spend (12 mo)", value: (a) => (a.stats?.spend_12mo != null ? Number(a.stats.spend_12mo) : null) },
  { id: "last_issue", label: "Last issue", value: (a) => a.stats?.last_issue_at || null, text: true },
  { id: "warranty", label: "Warranty", value: (a) => a.warranty_expires || null, text: true },
];

function compareAssets(a, b, column, order) {
  const av = column.value(a);
  const bv = column.value(b);
  const aEmpty = av === null || av === undefined || av === "";
  const bEmpty = bv === null || bv === undefined || bv === "";
  if (aEmpty || bEmpty) return aEmpty === bEmpty ? 0 : aEmpty ? 1 : -1;
  const result = column.text
    ? String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" })
    : av - bv;
  return order === "asc" ? result : -result;
}

function SummaryTile({ label, value, tone }) {
  const warn = tone === "warn";
  return (
    <div
      className={`rounded-[10px] border px-4 py-3 ${
        warn ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"
      }`}
    >
      <p className={`text-xs font-medium ${warn ? "text-amber-800" : "text-slate-500"}`}>{label}</p>
      <p className={`mt-1 text-2xl font-bold ${warn ? "text-amber-900" : "text-slate-900"}`}>{value}</p>
    </div>
  );
}

function ActionButton({ icon: Icon, children, onClick, primary }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium ${
        primary
          ? "bg-violet-700 hover:bg-violet-800 text-white"
          : "border border-violet-700 text-violet-700 hover:bg-violet-50"
      }`}
    >
      <Icon fontSize="small" />
      {children}
    </button>
  );
}

// Maintenance > Assets: one store's equipment registry. Managers only (the
// parent gates the toggle); the store picker lists stores where the user is
// a manager.
function AssetsView({ homeStore, stores, onOpenTicket, onNewTicketForAsset, refreshKey }) {
  const [storeId, setStoreId] = useState(() =>
    stores.some((s) => s.id === homeStore) ? homeStore : stores[0]?.id
  );
  useEffect(() => {
    if (!stores.some((s) => s.id === storeId)) {
      setStoreId(stores.some((s) => s.id === homeStore) ? homeStore : stores[0]?.id);
    }
  }, [stores, storeId, homeStore]);
  const storeName = stores.find((s) => s.id === storeId)?.store_name || "";

  const [assets, setAssets] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [selectedAssetUuid, setSelectedAssetUuid] = useState(null);
  const [drawerAsset, setDrawerAsset] = useState(undefined); // undefined closed, null new
  const [importOpen, setImportOpen] = useState(false);
  const [labelAssets, setLabelAssets] = useState(null);
  const [detailRefresh, setDetailRefresh] = useState(0);

  // Ignore a slow response for a store the user has since switched away from.
  const storeRef = useRef(storeId);
  storeRef.current = storeId;

  const fetchAssets = useCallback(
    async (showLoading = true) => {
      if (!storeId) return;
      const requestStore = storeId;
      if (showLoading) setIsLoading(true);
      try {
        const res = await getRightworkAPI(API_get_maintenance_assets, {
          store_id: homeStore,
          asset_store_id: requestStore,
          include_archived: "1",
        });
        if (storeRef.current !== requestStore) return;
        setAssets(res.data?.redux?.payload?.maintenance_assets || []);
        setCanEdit(Boolean(res.data?.redux?.payload?.can_edit));
      } catch (error) {
        if (storeRef.current === requestStore) toast.error("Failed to load assets");
      } finally {
        if (storeRef.current === requestStore) setIsLoading(false);
      }
    },
    [homeStore, storeId]
  );

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  // Ticket edits elsewhere on the page change asset rollups.
  useEffect(() => {
    if (refreshKey) {
      fetchAssets(false);
      setDetailRefresh((n) => n + 1);
    }
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSelectedAssetUuid(null);
    setLocationFilter("");
    setSearch("");
  }, [storeId]);

  const activeAssets = useMemo(() => assets.filter((a) => !a.is_archived), [assets]);
  const locations = useMemo(
    () => Array.from(new Set(activeAssets.map((a) => a.location).filter(Boolean))).sort(),
    [activeAssets]
  );
  const visibleAssets = useMemo(
    () =>
      assets.filter(
        (a) =>
          a.is_archived === showArchived &&
          (!locationFilter || a.location === locationFilter) &&
          assetMatches(a, search)
      ),
    [assets, showArchived, locationFilter, search]
  );

  // Same sorting behavior as the tickets table: click to sort ascending,
  // click again to flip.
  const [orderBy, setOrderBy] = useState("code");
  const [order, setOrder] = useState("asc");
  const handleSort = (columnId) => {
    const isAsc = orderBy === columnId && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnId);
  };
  const sortedAssets = useMemo(() => {
    const column = COLUMNS.find((c) => c.id === orderBy) || COLUMNS[0];
    const codeColumn = COLUMNS[0];
    return [...visibleAssets].sort(
      (a, b) => compareAssets(a, b, column, order) || compareAssets(a, b, codeColumn, "asc")
    );
  }, [visibleAssets, orderBy, order]);

  // Labels print for active assets matching the search and location, whatever
  // the Active/Archived filter shows (archived stickers no longer work).
  const printableAssets = useMemo(
    () =>
      activeAssets.filter(
        (a) => (!locationFilter || a.location === locationFilter) && assetMatches(a, search)
      ),
    [activeAssets, locationFilter, search]
  );

  const totals = useMemo(() => {
    let open = 0;
    let repeat = 0;
    let spend = 0;
    activeAssets.forEach((a) => {
      open += a.stats?.open_count || 0;
      if (a.stats?.repeat_issue) repeat += 1;
      spend += Number(a.stats?.spend_12mo || 0);
    });
    return { open, repeat, spend };
  }, [activeAssets]);

  const storePicker = stores.length > 1 && (
    <LabeledSelect
      id="asset-store"
      label="Store"
      className="w-56"
      value={storeId || ""}
      onChange={(e) => setStoreId(e.target.value)}
      options={stores.map((s) => ({ value: s.id, label: s.store_name }))}
    />
  );

  const drawers = (
    <>
      <AssetDrawer
        isOpen={drawerAsset !== undefined}
        onClose={() => setDrawerAsset(undefined)}
        asset={drawerAsset}
        homeStore={homeStore}
        storeId={storeId}
        onSaved={() => {
          fetchAssets(false);
          setDetailRefresh((n) => n + 1);
        }}
      />
      <AssetImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        homeStore={homeStore}
        storeId={storeId}
        storeName={storeName}
        onImported={() => fetchAssets(false)}
      />
      <AssetLabelSheet
        open={Boolean(labelAssets)}
        onClose={() => setLabelAssets(null)}
        assets={labelAssets || []}
        storeName={storeName}
      />
    </>
  );

  if (!storeId) {
    return (
      <div className="py-20 text-center text-slate-400">
        Assets are managed per location by managers. You don't manage any locations in this company.
      </div>
    );
  }

  if (selectedAssetUuid) {
    return (
      <>
        <AssetDetailView
          assetUuid={selectedAssetUuid}
          homeStore={homeStore}
          storeId={storeId}
          storeName={storeName}
          refreshKey={detailRefresh}
          onBack={() => setSelectedAssetUuid(null)}
          onEdit={(asset) => setDrawerAsset(asset)}
          onPrintLabel={(asset) => setLabelAssets([asset])}
          onNewTicket={onNewTicketForAsset}
          onOpenTicket={onOpenTicket}
          onChanged={() => fetchAssets(false)}
        />
        {drawers}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="flex items-end gap-3">{storePicker}</div>
        {canEdit && (
          <div className="flex items-center gap-3 flex-wrap">
            <ActionButton icon={UploadFileIcon} onClick={() => setImportOpen(true)}>
              Import CSV
            </ActionButton>
            {printableAssets.length > 0 && (
              <ActionButton icon={PrintIcon} onClick={() => setLabelAssets(printableAssets)}>
                Print QR labels
              </ActionButton>
            )}
            <ActionButton icon={AddIcon} primary onClick={() => setDrawerAsset(null)}>
              Add Asset
            </ActionButton>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-40">
          <LoadSpinner />
        </div>
      ) : assets.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <HandymanIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 2 }} />
          <p className="text-lg font-medium text-slate-500">Track your equipment</p>
          <p className="mt-1 max-w-md text-sm text-slate-400">
            Add your griddles, coolers, fryers and walk-ins to see each one's repair history and spend, and print
            QR stickers so your team can report problems on the spot.
          </p>
        </div>
      ) : (
        <>
          <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
            <SummaryTile label="Assets tracked" value={activeAssets.length} />
            <SummaryTile label="Open tickets on assets" value={totals.open} />
            <SummaryTile
              label="Repeat issues (90 days)"
              value={`${totals.repeat} asset${totals.repeat === 1 ? "" : "s"}`}
              tone={totals.repeat > 0 ? "warn" : undefined}
            />
            <SummaryTile label="Repair spend (12 months)" value={formatMoney(totals.spend) || "$0"} />
          </div>

          <div className="flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <TextField
                fullWidth
                placeholder="Search name, code, or area..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary", fontSize: 20 }} />,
                  endAdornment: search && (
                    <IconButton size="small" aria-label="Clear search" onClick={() => setSearch("")}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "42px",
                    backgroundColor: "white",
                    borderRadius: "8px",
                    fontSize: "14px",
                    "&.Mui-focused fieldset": { borderColor: "#6d28d9" },
                  },
                }}
              />
            </div>
            {locations.length > 0 && (
              <LabeledSelect
                id="asset-area-filter"
                label="Area"
                className="w-44"
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                options={[
                  { value: "", label: "All areas" },
                  ...locations.map((loc) => ({ value: loc, label: loc })),
                ]}
              />
            )}
            <LabeledSelect
              id="asset-status-filter"
              label="Status"
              className="w-36"
              value={showArchived ? "archived" : "active"}
              onChange={(e) => setShowArchived(e.target.value === "archived")}
              options={[
                { value: "active", label: "Active" },
                { value: "archived", label: "Archived" },
              ]}
            />
          </div>

          <div className="bg-white rounded-[10px] border border-slate-200 overflow-x-auto">
            <table className="w-full text-[13px]" style={{ minWidth: 1000 }}>
              <thead>
                <tr className="text-left text-xs text-slate-700">
                  {COLUMNS.map((col) => (
                    <th
                      key={col.id}
                      className="px-4 py-3 font-semibold border-b border-slate-200 whitespace-nowrap"
                      aria-sort={orderBy === col.id ? (order === "asc" ? "ascending" : "descending") : undefined}
                    >
                      <TableSortLabel
                        active={orderBy === col.id}
                        direction={orderBy === col.id ? order : "asc"}
                        onClick={() => handleSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleAssets.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-10 text-center text-slate-400">
                      No assets match these filters
                    </td>
                  </tr>
                ) : (
                  sortedAssets.map((a) => {
                    const expired = isWarrantyExpired(a.warranty_expires);
                    return (
                      <tr
                        key={a.uuid}
                        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                        onClick={() => setSelectedAssetUuid(a.uuid)}
                      >
                        <td className="px-4 py-3 text-slate-500 tabular-nums whitespace-nowrap">{a.code}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="font-semibold text-slate-900 text-left hover:text-violet-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedAssetUuid(a.uuid);
                              }}
                            >
                              {a.name}
                            </button>
                            {a.stats?.repeat_issue && (
                              <span className="px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-800 text-[11px] font-semibold whitespace-nowrap">
                                Repeat issue
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{a.location}</td>
                        <td className="px-4 py-3 text-slate-700">
                          {[a.manufacturer, a.model_number].filter(Boolean).join(" ")}
                        </td>
                        <td
                          className={`px-4 py-3 font-semibold ${
                            a.stats?.open_count ? "text-violet-700" : "text-slate-400"
                          }`}
                        >
                          {a.stats?.open_count || 0}
                        </td>
                        <td className="px-4 py-3 text-slate-700 tabular-nums">{a.stats?.tickets_12mo || 0}</td>
                        <td className="px-4 py-3 text-slate-700 tabular-nums">
                          {formatMoney(a.stats?.spend_12mo) || <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                          {formatShortDate(a.stats?.last_issue_at) || <span className="text-slate-300">—</span>}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap ${expired ? "text-red-700" : "text-slate-700"}`}>
                          {a.warranty_expires ? (
                            expired ? "Expired" : formatMonthYear(a.warranty_expires)
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      {drawers}
    </div>
  );
}

export default AssetsView;
